import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Button,
  CircularProgress,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { styled } from "@mui/material/styles";
import { TypographyNormal } from "../customComponent";
import { useDebounce } from "../hooks";
import { colorStyling } from "../../helpers/color";
import AssignItem from "../assign/AssignItem";
import ViewDetailViewer from "../viewDetails/viewer/ViewDetailViewer";
import { ActionMenuComponent } from "../assign/AssignedComponentActionMenu";
import { likeSearchInArray } from "../../helpers/arrayHelper";
import { logger } from "../../helpers/logger";

const PREFIX = 'ViewerListDialog';

const classes = {
  hover: `${PREFIX}-hover`,
  hoverPaper: `${PREFIX}-hoverPaper`
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const StyledBox = styled('div')(() => ({
  [`& .${classes.hover}`]: {
    "&:hover": {
      cursor: "pointer",
      textDecoration: "underline",
      color: colorStyling.primary,
    },
  },

  [`& .${classes.hoverPaper}`]: {
    "&:hover": { backgroundColor: colorStyling.white.hover },
  }
}));

const InputSearch = styled("input")(() => ({
  outline: "none",
  border: "none",
  fontSize: "16px",
  lineHeight: "26px",
  fontWeight: 400,
  width: "100%",
  marginLeft: "10px",
  fontFamily: "Inter, sans-serif",
}));

const BoxInput = styled(Box)(({ theme }) => ({
  border: `1px solid ${colorStyling.white.border}`,
  display: "flex",
  alignItems: "center",
  paddingLeft: theme.spacing(1.875),
  paddingRight: theme.spacing(1.875),
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  borderRadius: "6px",
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

const ViewerListDialog = ({ group, open, handleClose }) => {
  const { t } = useTranslation("group");
  const { t: tCommon } = useTranslation();

  const { user } = useSelector((state) => state.auth);
  const permission = user?.permission;
  const viewerPermission = permission?.screen;

  const [loading, setLoading] = useState(false);
  const [listViewer, setListViewer] = useState([]);
  const [selectedItem, setSelectedItem] = useState({});

  const [openDetailViewer, setOpenDetailViewer] = useState(false);
  const [anchorMenu, setAnchorMenu] = useState(null);
  const openMenu = Boolean(anchorMenu);

  useEffect(() => {
    if (open) {
      setListViewer(group.screens);
    } else {
      setSearch("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, group.screens]);

  /**
   * 1 Second delay after input value change to do API fetch
   */
  const [search, setSearch] = useState("");
  const debouncedSearchTerm = useDebounce(search, 1000);
  useEffect(
    () => {
      if (debouncedSearchTerm.trim().length === 0) {
        if (listViewer.length !== group.screens.length) {
          setListViewer(group.screens);
        }
        return;
      }

      try {
        setLoading(true);
        const results = likeSearchInArray(
          group.screens,
          "name",
          debouncedSearchTerm
        );

        setListViewer(results);
        setLoading(false);
      } catch (error) {
        logger.error(error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedSearchTerm] // Only call effect if debounced search term changes
  );

  const handleCloseDetailViewer = () => {
    setOpenDetailViewer(false);
  };

  const handleClickMenu = (event) => {
    setAnchorMenu(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorMenu(null);
  };

  return (
    <>
      <Dialog maxWidth="sm" open={open} onClose={handleClose} fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {t("viewerList.title")}
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent>
          <BoxInput>
            <SearchIcon
              fontSize="medium"
              sx={{ color: colorStyling.gray.normal }}
            />

            <InputSearch
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </BoxInput>

          <Box
            sx={{
              maxHeight: 300,
              overflowY: "auto",
              borderRadius: "6px",
              border: `1px solid ${colorStyling.white.border}`,
              mt: 1,
            }}
          >
            {loading ? (
              <Box
                display="flex"
                justifyContent="center"
                flexDirection="column"
                alignItems="center"
                sx={{ py: 4 }}
              >
                <CircularProgress
                  size={20}
                  thickness={3}
                  sx={{ color: colorStyling.primary }}
                />
                <TypographyNormal
                  sx={{
                    color: colorStyling.primary,
                    marginTop: "15px",
                    fontWeight: 300,
                  }}
                >
                  {tCommon("loadingDataList")}
                </TypographyNormal>
              </Box>
            ) : (
              <>
                {group && group?.screensCount >= 0 && (
                  <>
                    {listViewer.length === 0 ? (
                      <Box
                        display="flex"
                        justifyContent="center"
                        flexDirection="column"
                        alignItems="center"
                        sx={{ py: 4 }}
                      >
                        <TypographyNormal>
                          {t("viewerList.noData")}
                        </TypographyNormal>
                      </Box>
                    ) : (
                      <StyledBox display={"flex"} flexDirection={"column"}>
                        {listViewer.map((item) => (
                          <Box
                            className={classes.hoverPaper}
                            key={item.id}
                            sx={{
                              p: 2,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <AssignItem getName={"viewer"} data={item} />

                            <Box>
                              <IconButton
                                sx={{ color: colorStyling.black }}
                                onClick={(e) => {
                                  handleClickMenu(e);
                                  setSelectedItem(item);
                                }}
                              >
                                <MoreVertIcon />
                              </IconButton>
                            </Box>
                          </Box>
                        ))}
                      </StyledBox>
                    )}
                  </>
                )}
              </>
            )}
          </Box>

          <ActionMenuComponent
            open={openMenu}
            anchorEl={anchorMenu}
            handleClose={handleCloseMenu}
            type={"viewer"}
            item={selectedItem}
            sourceData={{
              ...group,
              type: "group",
              editPermission: viewerPermission?.update,
            }}
          />
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" fullWidth onClick={handleClose}>
            {tCommon("closeBtn")}
          </Button>
        </DialogActions>
      </Dialog>
      <ViewDetailViewer
        openDetail={openDetailViewer}
        viewer={selectedItem}
        onClose={handleCloseDetailViewer}
      />
    </>
  );
};

export default ViewerListDialog;
